import type { Notification } from './notificationService';

/**
 * Cible de navigation d'une notification, partagée entre NotificationBell
 * et NotificationsPage. `task` ouvre le GlobalTaskModal via taskModalStore.
 */
export type NotificationTarget =
  | { type: 'url'; url: string }
  | { type: 'messaging'; channelId: string; messageId: string | null }
  | { type: 'task'; taskId: string }
  | null;

// Onglet du dossier ciblé selon le type d'événement.
const DOSSIER_TAB_BY_TYPE: Record<string, string> = {
  dossier_file_added: '/documents',
  dossier_email_attached: '/documents',
  dossier_stage_changed: '/timeline',
  dossier_member_added: '/equipe',
  dossier_hearing_set: '/resume',
  hearing_reminder: '/resume',
  prescription_reminder: '/resume',
  email_grafted: '/documents?email=1',
};

export function getNotificationTarget(n: Notification): NotificationTarget {
  // Le destinataire n'a plus accès au dossier : pas de navigation.
  if (n.type === 'dossier_member_removed') return null;

  // Le mail en attente n'est pas encore rattaché à un dossier : la navigation
  // pointe vers la file de rattachement, désormais sur le tableau de bord.
  if (n.type === 'email_graft_pending') return { type: 'url', url: '/dashboard' };

  const meta = (n.metadata ?? {}) as Record<string, unknown>;

  const circuitId = typeof meta.circuit_id === 'string' ? meta.circuit_id : null;
  if (circuitId) return { type: 'url', url: `/parapheur/${circuitId}` };

  const ticketId = typeof meta.ticket_id === 'string' ? meta.ticket_id : null;
  if (ticketId) return { type: 'url', url: `/tickets/${ticketId}` };

  const channelId = typeof meta.channel_id === 'string' ? meta.channel_id : null;
  if (channelId) {
    const messageId = typeof meta.message_id === 'string' ? meta.message_id : null;
    return { type: 'messaging', channelId, messageId };
  }

  // Avant dossier_id : un rappel de tâche porte task_id ET dossier_id.
  const taskId = typeof meta.task_id === 'string' ? meta.task_id : null;
  if (taskId) return { type: 'task', taskId };

  const dossierId = typeof meta.dossier_id === 'string' ? meta.dossier_id : null;
  if (dossierId) {
    return { type: 'url', url: `/dossiers/${dossierId}${DOSSIER_TAB_BY_TYPE[n.type] ?? ''}` };
  }

  return null;
}

export const NOTIFICATION_TYPE_ICONS: Record<string, string> = {
  ticket_assigned: '📥',
  ticket_delegated: '🤝',
  ticket_queue_alert: '⚠️',
  ticket_unassigned: '↩️',
  ticket_status_changed: '🔄',
  ticket_note_added: '💬',
  mention: '💬',
  dm: '✉️',
  system: '🔔',
  task_reminder: '⏰',
  task_assigned: '📌',
  dossier_file_added: '📄',
  dossier_email_attached: '📎',
  dossier_stage_changed: '🗂️',
  dossier_hearing_set: '📅',
  hearing_reminder: '📅',
  prescription_reminder: '⚠️',
  dossier_member_added: '👥',
  dossier_member_removed: '👥',
  email_grafted: '📎',
  email_graft_pending: '📥',
  signature_to_sign: '✍️',
  signature_returned: '↩️',
  signature_new_version: '📄',
  signature_completed: '✅',
};

export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  ticket_assigned: 'Ticket assigné',
  ticket_delegated: 'Délégation',
  ticket_queue_alert: "File d'attente",
  ticket_unassigned: 'Désassignation',
  ticket_status_changed: 'Changement de statut',
  ticket_note_added: 'Nouvelle note',
  mention: 'Mention',
  dm: 'Message direct',
  system: 'Système',
  task_reminder: "Rappel d'échéance",
  task_assigned: 'Tâche assignée',
  dossier_file_added: 'Document ajouté',
  dossier_email_attached: 'Email rattaché',
  dossier_stage_changed: 'Étape du dossier',
  dossier_hearing_set: 'Audience fixée',
  hearing_reminder: "Rappel d'audience",
  prescription_reminder: 'Prescription',
  dossier_member_added: 'Ajout au dossier',
  dossier_member_removed: 'Retrait du dossier',
  email_grafted: 'Email rattaché',
  email_graft_pending: 'Mail à rattacher',
  signature_to_sign: 'À signer',
  signature_returned: 'Renvoyé',
  signature_new_version: 'Nouvelle version',
  signature_completed: 'Signé',
};
